import { openNextDashboardTab } from '../actions/social/leaderboard-actions'
import doSave from '../actions/save'
import { activateNextPane, togglePlay } from '../actions/play-actions'
import { Pane } from '../constants/constants'
import modalsSlice from '../reducers/modalsSlice'
import settingsSlice from '../reducers/settingsSlice'
import { clearWordScoringClasses } from '../util/score-utils'
import trackMixer from './TrackMixer'
import { AppDispatch } from '../reducers'
import { Word } from '../types'
import GamepadService from './GamepadService'
import Gamepad from './Gamepad'
import userManager from './UserManager'
import Gamer from './Gamer'

type ButtonCallbackMap = { [button: string]: () => void }

const simulateKeyPress = (key: string, code: number) => {
  const tabKeyEvent = new KeyboardEvent('keydown', {
    key: key,
    code: key,
    keyCode: code,
    which: code,
    bubbles: true,
  })
  document.activeElement?.dispatchEvent(tabKeyEvent)
}

class KeyBindings {
  _dispatch: AppDispatch | null
  _gamepadService: GamepadService
  _containerStack: Element[]
  _navCallback: ((horizStep: number, vertStep: number) => void) | null
  constructor() {
    this._dispatch = null
    this._gamepadService = new GamepadService()
    this._containerStack = []
    this._navCallback = null
  }
  get dispatch(): AppDispatch {
    if (!this._dispatch) {
      throw new Error('dispatch not set')
    }
    return this._dispatch
  }
  init(dispatch: AppDispatch) {
    this._dispatch = dispatch
    window.addEventListener('keydown', this.handleKeydown.bind(this))
    window.addEventListener('keyup', this.handleKeyup.bind(this))
    this._gamepadService.on('connect', (gamepad: Gamepad) => {
      console.log('gamepad', gamepad)
      this.addGamepadListeners(gamepad)
    })
  }
  onModalContainerRef(container: Element | null, isPane?: boolean) {
    if (container && container !== this.currentContainer) {
      if (isPane) {
        this._containerStack = [container]
      } else {
        this._containerStack.push(container)
      }
      this.focusNextField(1, 0)
    }
  }
  popModal() {
    this._containerStack.pop()
    this.focusNextField(1, 0) // TODO: ideally, this should be previously selected field
  }
  clearContainers() {
    this._containerStack = []
  }
  get currentContainer() {
    const topContainerIndex = this._containerStack.length - 1
    return topContainerIndex >= 0 ? this._containerStack[topContainerIndex] : null
  }
  focusNextField(horizStep: number, vertStep: number) {
    if (this._navCallback) {
      this._navCallback(horizStep, vertStep)
      return
    }
    const container = this.currentContainer
    if (!container) {
      return
    }
    // Get a list of all focusable elements
    const focusableElements = Array.from(
      container.querySelectorAll('input, select, textarea, button, a[href], [tabindex]')
    ).filter((elem) => {
      const htmlElem = elem as HTMLInputElement
      return htmlElem.tabIndex !== -1 && !htmlElem.disabled
    })
    const startingField = document.activeElement
    // Get the index of the current field
    const currentIndex = startingField ? focusableElements.indexOf(startingField) : -1

    // Move to the next field and handle wrapping around to the first element
    const step = horizStep !== 0 ? horizStep : vertStep
    const nextIndex = (currentIndex + step + focusableElements.length) % focusableElements.length
    const nextField = focusableElements[nextIndex]

    // Set focus to the next field
    if (nextField) {
      // console.log('nextField', nextField)
      ;(nextField as HTMLElement).focus()
    } else if (startingField) {
      ;(startingField as HTMLElement).blur()
    }
  }
  get isInModal() {
    return this._containerStack.length > 1
  }
  get isInSelect() {
    const activeElementIsSelect = document.activeElement instanceof HTMLSelectElement
    return activeElementIsSelect
  }
  get isInInput() {
    const activeElementIsInput = document.activeElement instanceof HTMLInputElement
    return activeElementIsInput
  }
  getActivePlayerAt(controllerIndex: number) {
    const activePlayers = trackMixer._gamers.filter(({ isActive }) => isActive)
    const numPlayers = activePlayers.length
    const activePlayerIndex =
      numPlayers > controllerIndex ? controllerIndex : numPlayers > 0 ? 0 : -1
    return activePlayerIndex >= 0 ? activePlayers[activePlayerIndex] : null
  }

  handleKeyup(event: KeyboardEvent) {
    const isRight = event.location === KeyboardEvent.DOM_KEY_LOCATION_RIGHT
    const activePlayer = this.getActivePlayerAt(isRight ? 1 : 0)
    if (!activePlayer || this.isInModal) {
      return
    }
    switch (event.keyCode) {
      case 91: // LEFT CMD key
      case 93: // RIGHT CMD key
      case 16: // SHIFT key
        activePlayer.gamer.releaseBlast(isRight)
        break

      case 18: // Alt key
        this.dispatch(settingsSlice.actions.setIsOptionKey({ isOptionKey: false }))
        break

      default:
    }
  }
  handleKeydown(event: KeyboardEvent) {
    const { metaKey, keyCode, altKey, shiftKey, key, location } = event
    if (keyCode === 27) {
      // Esc key
      this.handleEscape()
      event.preventDefault()
      return
    }
    const isRight = location === KeyboardEvent.DOM_KEY_LOCATION_RIGHT
    const activePlayer = this.getActivePlayerAt(isRight ? 1 : 0)
    // if (!activePlayer || this.isInModal) {
    if (!activePlayer) {
      return
    }
    const { gamer: player, isActive } = activePlayer
    let handled = false

    if (metaKey) {
      switch (keyCode) {
        case 83: // 's' key
          this.dispatch(doSave({ isDownload: altKey, gamer: player }))
          handled = true
          break
        case 85: // 'u' key
          // app.gameSettings.doSync(event.altKey); // TODO
          handled = true
          break
        case 186: // ';' key
          // app.gameSettings.trackInfoModal.toggle(true); // TODO
          handled = true
          break
        case 39: // right arrow
          if (!this.isInModal) {
            this.dispatch(activateNextPane({ direction: 1 }))
          }
          handled = true
          break
        default:
      }
    }
    if (handled) {
      event.preventDefault()
      return
    }
    handled = true
    switch (keyCode) {
      case 32: // SPACE key
        handled = this.handleSpace()
        break

      case 91: // LEFT CMD key
      case 93: // RIGHT CMD key
      case 16: // SHIFT key
        player.blast(isRight)
        break

      case 13: // RETURN key
        if (player.hasFocus) {
          trackMixer.rewindToStart()
        } else {
          const activeElement = document.activeElement as HTMLElement
          if (activeElement) {
            if (activeElement instanceof HTMLInputElement) {
              if (activeElement.type === 'text' || activeElement.type === 'number') {
              }
              activeElement.blur()
            } else {
              activeElement.click()
            }
          }
        }
        break

      case 39: // Right arrow
        if (player.hasFocus) {
          const isSeek = !shiftKey
          player._loadNextWord(isSeek)
        } else if (true || !isActive) {
          this.focusNextField(1, 0)
        }
        break

      case 37: // Left arrow
        if (player.hasFocus) {
          const isPlayHead = shiftKey
          player._loadPrevWord(isPlayHead)
        } else if (true || !isActive) {
          this.focusNextField(-1, 0)
        }
        break

      case 8: // backspace/delete key
        if (altKey) {
          player.clearTiming()
        } else {
          if (player.target.elem) {
            const word = player.targetWord
            if (word) {
              if (trackMixer.isVizMode) {
                clearWordScoringClasses(word as Word)
              } else {
                player.clearWord(word as Word, true)
                player._updateDirty()
              }
            }
          }
          player._loadPrevWord()
          player.stop()
        }
        break

      case 38: // Up arrow
        handled = this.handleArrowUp(player, shiftKey)
        break

      case 40: // Down arrow
        handled = this.handleArrowDown(player, shiftKey)
        break

      case 18: // Alt key
        this.dispatch(settingsSlice.actions.setIsOptionKey({ isOptionKey: true }))
        break

      case 27: // Esc key
        this.handleEscape()
        break

      case 191: // / key
        this.dispatch(modalsSlice.actions.toggleHelpModal(true))
        break

      default:
        handled = false
        if (trackMixer.isSpellMode) {
          player.appendSpelling(key)
        }
    }

    if (handled) {
      event.preventDefault()
    }
  }

  get isPubMode() {
    return userManager.audience === 'pub'
  }
  get isCouchMode() {
    return userManager.audience === 'couch'
  }
  handleSpace() {
    if (!this.isInModal && !trackMixer.isSpellMode) {
      this.dispatch(togglePlay(false))
      return true
    }
    return false
  }
  handleEscape() {
    const closeIcons = document.querySelectorAll('.closeIcon.top')
    if (closeIcons.length) {
      ;(closeIcons[0] as HTMLElement).click()
    }
  }
  handleArrowUp(player: Gamer, shiftKey = false) {
    if (player.hasFocus && !this.isInModal) {
      const isPlayHead = shiftKey
      player._loadPrevLine(isPlayHead)
    } else {
      if (this.isInSelect) {
        return false
      }
      this.focusNextField(0, -1)
    }
    return true
  }
  handleArrowDown(player: Gamer, shiftKey = false) {
    if (player.hasFocus && !this.isInModal) {
      const isSeek = !shiftKey
      player._loadNextLine(isSeek)
    } else {
      if (this.isInSelect) {
        return false
      }
      this.focusNextField(0, 1)
    }
    return true
  }
  addGamepadListeners(gamepad: Gamepad) {
    const { index } = gamepad
    const { _gamers: gamers } = trackMixer
    const gamerIndex = index < gamers.length ? index : 0
    const gameController = gamers[gamerIndex]
    const gamer = gameController.gamer
    const isPubModePlayer = () => {
      return this.isPubMode && gameController.isActive
    }
    const canNavigate = () => {
      return !this.isPubMode || !gameController.isActive
    }
    const escape = () => {
      if (isPubModePlayer()) {
        return // pub mode player can't dismiss modals
      }
      this.handleEscape()
    }
    const nextPane = () => {
      if (isPubModePlayer()) {
        return // pub mode player can't switch panes
      }
      this.dispatch(activateNextPane({ direction: 1 }))
    }
    const clickSelected = () => {
      if (isPubModePlayer() && gamepad.index !== 0) {
        return // only left-hand player can select track
      }
      ;(document.activeElement as HTMLElement).click()
    }
    const clearOrRewind = () => {
      if (isPubModePlayer()) {
        return // pub mode player can't clear or rewind
      }
      if (this.isInModal) {
        return
      }
      if (!trackMixer.isPlaying) {
        trackMixer.clearGamerTimings()
      }
      trackMixer.rewindToStart()
    }
    const unpress = (isRight: boolean) => {
      gamer.releaseBlast(isRight)
    }
    const blast = (isRight: boolean) => {
      if (trackMixer.isBlasting) {
        if (gameController.isActive) {
          gamer.blast(isRight)
        }
      } else {
        const isRestrictDashboardToInactiveControllers = this.isPubMode || this.isCouchMode
        if (isRestrictDashboardToInactiveControllers && gameController.isActive) {
          gamer.blast(isRight)
        } else {
          this.dispatch(openNextDashboardTab(isRight))
        }
      }
    }
    /*
      We have to figure out whether to focus next/prev field or load next/prev LINE
      During playback, load next/prev word unless we're pub player, or a navigator and not playing
      If we're a navigator, also load next/prev line if we're stopped and focused on the play area,
      otherwise focus next/prev field
      If we're a player and play is stopped, focus next/prev field if we're in a modal (presumably Track Gallery)
     */
    const moveVert = (isDown: boolean) => {
      if (canNavigate() && gamer.hasFocus) {
        if (isDown) {
          gamer._loadNextLine(true)
        } else {
          gamer._loadPrevLine(false)
        }
      } else if (canNavigate() || this.isInModal) {
        if (this.isInSelect || this.isInInput) {
          simulateKeyPress(isDown ? 'ArrowDown' : 'ArrowUp', isDown ? 40 : 38)
        } else {
          if (isPubModePlayer() && gamepad.index !== 0) {
            return // only left-hand pub mode player can move around in modal (i.e. to select track)
          }
          this.focusNextField(0, isDown ? 1 : -1)
        }
      }
    }

    /*
      We have to figure out whether to focus next/prev field or load next/prev WORD
      During playback, load next/prev word unless we're a navigator and not playing
      If we're a navigator, also load next/prev word if we're stopped and focused on the play area,
      otherwise focus next/prev field
      If we're a player and play is stopped, focus next/prev field if we're in a modal (presumably Track Gallery)
     */
    const moveHoriz = (isRight: boolean) => {
      const isJumpWord =
        (isPubModePlayer() && trackMixer.isPlaying) || (canNavigate() && gamer.hasFocus)
      if (isJumpWord) {
        if (isRight) {
          gamer._loadNextWord(true)
        } else {
          gamer._loadPrevWord(false)
        }
      } else if (canNavigate() || this.isInModal) {
        if (isPubModePlayer() && gamepad.index !== 0) {
          return // only left-hand pub mode player can move around in modal (i.e. to select track)
        }
        this.focusNextField(isRight ? 1 : -1, 0)
      }
    }
    // TODO: perhaps allow/use in some admin mode?
    // const refreshPage = () => {
    //   if (!player.isPlaying) {
    //     // eslint-disable-next-line
    //     window.location.href = window.location.href
    //   }
    // }
    const playOrPause = () => {
      if (gamepad.index === 0 && isPubModePlayer()) {
        const rightHandPlayer = this.getActivePlayerAt(1)
        if (rightHandPlayer && rightHandPlayer.gamer.gamerIndex === 1) {
          return // only right-hand of two-player pub mode can start track
        }
      }
      this.handleSpace()
    }
    const mappings: ButtonCallbackMap = {
      button0: () => {
        console.log('button0 (South) rewind/clear')
        clearOrRewind()
      },
      button1: () => {
        console.info('button1 (East) next pane')
        nextPane()
      },
      button2: () => {
        console.log('button2 (West) select footer')
        this.dispatch(activateNextPane({ pane: Pane.FOOTER }))
      },
      button3: () => {
        console.log('button3 (North) escape')
        escape()
      },
      button4: () => {
        // console.log(`${gamerIndex}: button4 (front top left)`)
        blast(false)
      },
      button5: () => {
        // console.log(`${gamerIndex}: button5 (front top right)`)
        blast(true)
      },
      button6: () => {
        // console.log('button6 (front bottom left)')
        blast(false)
      },
      button7: () => {
        // console.log('button7 (front bottom right)')
        blast(true)
      },
      button8: () => {
        // console.log(`${gamepad.index}: button8 (select)`)
        clickSelected()
      },
      button9: () => {
        // console.log('button9 (start)')
        playOrPause()
      },
      button10: () => {
        // console.log('button10')
      },
      button11: () => {
        // console.log('button11')
      },
      button12: () => {
        // console.log('button12 (up)')
        moveVert(false)
      },
      button13: () => {
        // console.log('button13 (down)')
        moveVert(true)
      },
      button14: () => {
        // console.log('button14 (left)')
        moveHoriz(false)
      },
      button15: () => {
        // console.log('button15 (right)')
        moveHoriz(true)
      },
      button16: () => {
        // console.log('button16')
      },
      button17: () => {
        // console.log('button17')
      },
      left0: () => {
        // console.log('left0')
        moveHoriz(false)
      },
      right0: () => {
        // console.log('right0')
        moveHoriz(true)
      },
      up0: () => {
        // console.log('up0')
        moveVert(false)
      },
      down0: () => {
        // console.log('down0')
        moveVert(true)
      },
      left1: () => {
        // console.log('left1')
      },
      right1: () => {
        // console.log('right1')
      },
      up1: () => {
        // console.log('up1')
      },
      down1: () => {
        // console.log('down1')
      },
    }
    const afterMappings: ButtonCallbackMap = {
      button4: () => {
        // console.log('button4 after')
        unpress(false)
      },
      button5: () => {
        // console.log('button5 after')
        unpress(true)
      },
      button6: () => {
        // console.log('button6 after')
        unpress(false)
      },
      button7: () => {
        // console.log('button7 after')
        unpress(true)
      },
    }

    Object.keys(mappings).forEach((key) => {
      // TODO: because gamepad.before will just log a generic error if button doesn't exist
      // (e.g. button16 and 17 with Mika's "havit" controller)
      // we can't easily handle. we could hardcode known cases to provide better logging or feedback
      // -- or perhaps file an issue or contribute a PR to improve error-handling for the module
      gamepad.before(key, mappings[key])
    })
    Object.keys(afterMappings).forEach((key) => {
      // TODO: because gamepad.before will just log a generic error if button doesn't exist
      // (e.g. button16 and 17 with Mika's "havit" controller)
      // we can't easily handle. we could hardcode known cases to provide better logging or feedback
      // -- or perhaps file an issue or contribute a PR to improve error-handling for the module
      gamepad.after(key, afterMappings[key])
    })
  }
}

const defaultKeyBindings = new KeyBindings()
export default defaultKeyBindings
